import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

/**
 * VIB-663e: tilvalgsseksjoner på tilbud.
 * Hver seksjon har eget navn og samler `offer_lines.section_id`. Linjer uten
 * section_id behandles som hovedbudsjett i UI/eksport.
 */

export type OfferSectionType = "main" | "option";

export interface OfferSection {
  id: string;
  offer_id: string;
  name: string;
  section_type: OfferSectionType;
  sort_order: number;
  /** VIB-700: fritekst per tilvalg, vises i eksport. NULL = ingen tekst. */
  custom_terms_text: string | null;
  created_at: string;
}

export function useOfferSections(offerId: string | undefined) {
  return useQuery({
    queryKey: ["offer-sections", offerId],
    enabled: !!offerId,
    queryFn: async (): Promise<OfferSection[]> => {
      const { data, error } = await supabase
        .from("offer_sections")
        .select("*")
        .eq("offer_id", offerId!)
        .order("sort_order")
        .order("created_at");
      if (error) throw error;
      return (data ?? []) as OfferSection[];
    },
  });
}

export function useCreateOfferSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      offer_id: string;
      name: string;
      section_type?: OfferSectionType;
      sort_order?: number;
    }): Promise<OfferSection> => {
      const { data, error } = await supabase
        .from("offer_sections")
        .insert({
          offer_id: params.offer_id,
          name: params.name,
          section_type: params.section_type ?? "option",
          sort_order: params.sort_order ?? 0,
        })
        .select()
        .single();
      if (error) throw error;
      return data as OfferSection;
    },
    onSuccess: async (section) => {
      await invalidateFor(queryClient, "offer_sections", { offerId: section.offer_id });
    },
  });
}

export function useUpdateOfferSection(offerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      id: string;
      patch: Partial<
        Pick<OfferSection, "name" | "sort_order" | "section_type" | "custom_terms_text">
      >;
    }): Promise<void> => {
      const { error } = await supabase
        .from("offer_sections")
        .update(params.patch)
        .eq("id", params.id);
      if (error) throw error;
    },
    onSuccess: async () => {
      if (offerId) {
        await invalidateFor(queryClient, "offer_sections", { offerId });
      }
    },
  });
}

export function useDeleteOfferSection(offerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      // FK på offer_lines.section_id er ON DELETE SET NULL, så linjer
      // beholdes og flyttes til hovedbudsjett.
      const { error } = await supabase.from("offer_sections").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      if (offerId) {
        await invalidateFor(queryClient, "offer_sections", { offerId });
        await invalidateFor(queryClient, "offer_lines", { offerId });
      }
    },
  });
}
